/* eslint-disable react-hooks/exhaustive-deps */
import { Fragment, useEffect, useMemo, useState } from "react";
import { SHIFTIQ_API, getAuthHeaders, isAbortError } from "../../api/client";
import { pageSignal } from "../../routing/pageAbort";

function TraceSearch({ value, onChange, placeholder }) {
  return (
    <input
      className="admin-search"
      style={{ width: "100%", maxWidth: "100%", marginBottom: 12 }}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
    />
  );
}

function matchesQuery(row, q, fields) {
  if (!q) return true;
  const hay = fields.map((f) => String(row[f] ?? "").toLowerCase()).join(" ");
  return hay.includes(q);
}

function formatQtyWithUnit(qty, unit) {
  const n = Number(qty || 0);
  const formatted = Number.isFinite(n) ? n.toFixed(2) : "0.00";
  const u = String(unit || "").trim();
  return u ? `${formatted} ${u}` : formatted;
}

function formatSoldUnits(qty, t) {
  if (qty == null || qty === "") return null;
  const n = Number(qty);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `${n.toFixed(2)} ${t("units", "units")}`;
}

function formatEur(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `€${n.toFixed(2)}`;
}

function formatPct(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(1)}%`;
}

function roleBadge(role, t) {
  const r = String(role || "").toUpperCase();
  const isMod = r === "MODIFIER";
  return (
    <span
      className={`procureiq-line-role ${
        isMod ? "procureiq-line-role-modifier" : "procureiq-line-role-menu"
      }`}
    >
      {isMod ? t("MODIFIER", "MODIFIER") : t("MENU ITEM", "MENU ITEM")}
    </span>
  );
}

function sellableKey(s) {
  return `${s.material_product_id}|${s.line_role}|${s.unified_item_id ?? ""}`;
}

/**
 * Sales-side twin of ForecastLeafContribPanel.
 * data = GET /consumption/day payload (leaves + sellables).
 */
export function ConsumptionDayPanel({
  data,
  t,
  locationId,
  businessDate,
  view = "materials",
  onViewChange,
  onRefresh,
  refreshBusy,
  canRefresh,
}) {
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({});
  const [contribByLeaf, setContribByLeaf] = useState({});
  const [contribLoading, setContribLoading] = useState({});
  const [contribError, setContribError] = useState({});
  const [ordersByKey, setOrdersByKey] = useState({});
  const [ordersLoading, setOrdersLoading] = useState({});
  const [ordersError, setOrdersError] = useState({});
  const [openOrdersKey, setOpenOrdersKey] = useState(null);

  useEffect(() => {
    setExpanded({});
    setContribByLeaf({});
    setContribLoading({});
    setContribError({});
    setOrdersByKey({});
    setOrdersLoading({});
    setOrdersError({});
    setOpenOrdersKey(null);
    setQ("");
  }, [locationId, businessDate]);

  useEffect(() => {
    setExpanded({});
    setOpenOrdersKey(null);
    setQ("");
  }, [view]);

  const leafRows = useMemo(() => {
    if (!data?.available) return [];
    const query = q.trim().toLowerCase();
    return (data.leaves || [])
      .map((leaf) => {
        const hit = matchesQuery(leaf, query, ["item_name", "item_id", "base_unit"]);
        if (query && !hit) return null;
        return { ...leaf, _key: leaf.item_id };
      })
      .filter(Boolean);
  }, [data, q]);

  const sellableRows = useMemo(() => {
    if (!data?.available) return [];
    const query = q.trim().toLowerCase();
    return (data.sellables || [])
      .map((s) => {
        const hit = matchesQuery(s, query, [
          "name",
          "material_product_id",
          "line_role",
          "unified_item_id",
        ]);
        if (query && !hit) return null;
        return { ...s, _key: sellableKey(s) };
      })
      .filter(Boolean);
  }, [data, q]);

  const loadMaterial = async (itemId) => {
    if (!locationId || !businessDate || !itemId) return;
    if (contribByLeaf[itemId] || contribLoading[itemId]) return;
    setContribLoading((prev) => ({ ...prev, [itemId]: true }));
    setContribError((prev) => ({ ...prev, [itemId]: null }));
    try {
      const params = new URLSearchParams({
        location_id: String(locationId),
        date: businessDate,
        item_id: itemId,
      });
      const res = await fetch(
        `${SHIFTIQ_API}/api/procureiq/consumption/day/material?${params}`,
        { headers: getAuthHeaders(), signal: pageSignal() }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      const body = await res.json();
      setContribByLeaf((prev) => ({ ...prev, [itemId]: body }));
    } catch (err) {
      if (isAbortError(err)) return;
      setContribError((prev) => ({
        ...prev,
        [itemId]: err?.message || String(err),
      }));
    } finally {
      setContribLoading((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  const toggleLeaf = (itemId) => {
    setExpanded((prev) => {
      const nextOpen = !prev[itemId];
      if (nextOpen) loadMaterial(itemId);
      return { ...prev, [itemId]: nextOpen };
    });
  };

  const toggleSellable = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const ordersCacheKey = (c) =>
    `${c.material_product_id}|${c.line_role}|${c.unified_item_id ?? ""}`;

  const loadOrders = async (c) => {
    const key = ordersCacheKey(c);
    setOpenOrdersKey(key);
    if (ordersByKey[key] || ordersLoading[key]) return;
    setOrdersLoading((prev) => ({ ...prev, [key]: true }));
    setOrdersError((prev) => ({ ...prev, [key]: null }));
    try {
      const params = new URLSearchParams({
        location_id: String(locationId),
        date: businessDate,
        material_product_id: String(c.material_product_id || ""),
        line_role: String(c.line_role || "MENU_ITEM"),
      });
      if (c.unified_item_id != null) {
        params.set("unified_item_id", String(c.unified_item_id));
      }
      const res = await fetch(
        `${SHIFTIQ_API}/api/procureiq/consumption/day/orders?${params}`,
        { headers: getAuthHeaders(), signal: pageSignal() }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      const body = await res.json();
      setOrdersByKey((prev) => ({ ...prev, [key]: body }));
    } catch (err) {
      if (isAbortError(err)) return;
      setOrdersError((prev) => ({
        ...prev,
        [key]: err?.message || String(err),
      }));
    } finally {
      setOrdersLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  if (!data) return null;

  if (!data.available) {
    return (
      <div className="card-box">
        <p style={{ fontWeight: 800, fontSize: 14, marginBottom: 8 }}>
          {t("No consumption data", "Δεν υπάρχουν δεδομένα κατανάλωσης")}
        </p>
        <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 8 }}>
          {data.reason || "—"}
        </p>
        <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
          {t(
            "Apply migrations/create_procureiq_consumption_day.sql if tables are missing.",
            "Εφαρμόστε το migration create_procureiq_consumption_day.sql αν λείπουν οι πίνακες."
          )}
        </p>
      </div>
    );
  }

  const netSales = Number(data.net_sales_eur);
  const consumption = Number(data.cost_eur_total);
  const foodPct = data.food_cost_pct != null ? Number(data.food_cost_pct) : null;
  const isSoldView = view === "sold";

  return (
    <div className="card-box procureiq-send-block procureiq-trace-panel">
      <div
        className="procureiq-consumption-summary"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 10,
          marginBottom: 14,
          flexShrink: 0,
        }}
      >
        {[
          {
            label: t("Net sales", "Καθαρές πωλήσεις"),
            value: Number.isFinite(netSales) ? `€${netSales.toFixed(2)}` : "—",
            hint:
              Array.isArray(data.sales_location_ids) && data.sales_location_ids.length > 1
                ? t(
                    `Sum of ${data.sales_location_ids.length} storefronts`,
                    `Άθροισμα ${data.sales_location_ids.length} καταστημάτων`
                  )
                : null,
          },
          {
            label: t("Consumption", "Κατανάλωση"),
            value: Number.isFinite(consumption) ? `€${consumption.toFixed(2)}` : "—",
            hint: null,
          },
          {
            label: t("Food cost", "Food cost"),
            value: foodPct != null && Number.isFinite(foodPct) ? `${foodPct.toFixed(1)}%` : "—",
            hint: null,
          },
        ].map((cell) => (
          <div
            key={cell.label}
            style={{
              padding: "12px 14px",
              borderRadius: 10,
              border: "1px solid var(--border)",
              background: "var(--bg-muted, #f8fafc)",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--text-muted)",
                textTransform: "uppercase",
                letterSpacing: 0.3,
                marginBottom: 6,
              }}
            >
              {cell.label}
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: "var(--text-primary)" }}>
              {cell.value}
            </div>
            {cell.hint ? (
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                {cell.hint}
              </div>
            ) : null}
          </div>
        ))}
      </div>

      <div
        className="procureiq-tabs"
        style={{ marginBottom: 12, flexShrink: 0 }}
        role="tablist"
        aria-label={t("Consumption view", "Προβολή κατανάλωσης")}
      >
        <button
          type="button"
          role="tab"
          aria-selected={!isSoldView}
          className={`procureiq-tab ${!isSoldView ? "procureiq-tab-active" : ""}`}
          onClick={() => onViewChange?.("materials")}
        >
          {t("By material", "Ανά ύλη")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={isSoldView}
          className={`procureiq-tab ${isSoldView ? "procureiq-tab-active" : ""}`}
          onClick={() => onViewChange?.("sold")}
        >
          {t("By sold item", "Ανά sold είδος")}
        </button>
      </div>

      <p
        style={{
          fontSize: 12,
          color: "var(--text-muted)",
          marginBottom: 8,
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        {isSoldView
          ? t(
              "One row per menu item / modifier sold that day. Expand for raw materials × sold qty. Plate food cost = plate cost ÷ undiscounted list net (ex-VAT), not promo averages.",
              "Μία γραμμή ανά είδος μενού / modifier της ημέρας. Ανοίξτε για πρώτες ύλες × sold qty. Plate food cost = κόστος πιάτου ÷ καθαρή τιμή καταλόγου (χωρίς έκπτωση), όχι μ.ο. προσφορών."
            )
          : t(
              "Actual sales × recipe. One row per raw material for the selected day. Expand for menu sources; click a sold item for orders. Audit only — not used for ordering.",
              "Πραγματικές πωλήσεις × συνταγή. Μία γραμμή ανά πρώτη ύλη για την ημέρα. Ανοίξτε για πηγές μενού· κλικ σε sold είδος για παραγγελίες. Μόνο audit."
            )}
      </p>
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: "var(--text-muted)",
          marginBottom: 10,
          flexShrink: 0,
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
          alignItems: "center",
        }}
      >
        <span>
          {isSoldView
            ? `${data.sellable_count ?? (data.sellables || []).length} ${t("sold items", "sold είδη")}`
            : `${data.leaf_count} ${t("leaves", "πρώτες ύλες")}`}
          {" · "}
          {data.contrib_row_count} {t("contrib rows", "γραμμές συνεισφοράς")}
          {data.cost_eur_total != null ? ` · €${Number(data.cost_eur_total).toFixed(2)}` : ""}
        </span>
        {data.computed_at ? (
          <span style={{ fontWeight: 600 }}>
            {t("Updated", "Ενημ.")} {String(data.computed_at).replace("T", " ").slice(0, 19)}
            {data.from_cache
              ? ` · ${t("cached", "cache")}`
              : ` · ${t("recomputed", "υπολογίστηκε")}`}
          </span>
        ) : null}
        {canRefresh ? (
          <button
            type="button"
            className="btn-secondary"
            style={{ fontSize: 12, padding: "4px 10px" }}
            disabled={refreshBusy}
            onClick={() => onRefresh?.()}
          >
            {refreshBusy
              ? t("Refreshing…", "Ανανέωση…")
              : t("Refresh", "Ανανέωση")}
          </button>
        ) : null}
      </div>
      <div style={{ flexShrink: 0 }}>
        <TraceSearch
          value={q}
          onChange={setQ}
          placeholder={
            isSoldView
              ? t("Search sold item…", "Αναζήτηση sold είδους…")
              : t("Search leaf…", "Αναζήτηση πρώτης ύλης…")
          }
        />
      </div>

      {isSoldView ? (
        !sellableRows.length ? (
          <div style={{ textAlign: "center", padding: "24px 12px" }}>
            <p style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>
              {t("No sold items for this day", "Κανένα sold είδος για αυτή την ημέρα")}
            </p>
          </div>
        ) : (
          <div className="procureiq-trace-table-wrap">
            <table className="procureiq-trace-table">
              <thead>
                <tr>
                  <th style={{ width: 28 }} aria-hidden />
                  <th>{t("Sold item", "Sold είδος")}</th>
                  <th style={{ textAlign: "right" }}>{t("Sold", "Sold")}</th>
                  <th style={{ textAlign: "right" }}>{t("Plate cost", "Κόστος πιάτου")}</th>
                  <th style={{ textAlign: "right" }}>{t("Net price", "Καθαρή τιμή")}</th>
                  <th style={{ textAlign: "right" }}>{t("Plate FC", "FC πιάτου")}</th>
                  <th style={{ textAlign: "right" }}>{t("Day cost", "Κόστος ημέρας")}</th>
                </tr>
              </thead>
              <tbody>
                {sellableRows.map((s) => {
                  const isOpen = Boolean(expanded[s._key]);
                  return (
                    <Fragment key={s._key}>
                      <tr
                        className="procureiq-contrib-leaf-row"
                        style={{ cursor: "pointer" }}
                        onClick={() => toggleSellable(s._key)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleSellable(s._key);
                          }
                        }}
                        tabIndex={0}
                        aria-expanded={isOpen}
                      >
                        <td style={{ verticalAlign: "middle", paddingRight: 0 }}>
                          <span className={`procureiq-chevron ${isOpen ? "is-open" : ""}`}>›</span>
                        </td>
                        <td>
                          <div style={{ fontWeight: 700 }}>{s.name}</div>
                          <div
                            style={{
                              fontSize: 11,
                              color: "var(--text-muted)",
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 6,
                              alignItems: "center",
                              marginTop: 3,
                            }}
                          >
                            {roleBadge(s.line_role, t)}
                            {s.unified_item_id != null ? (
                              <span>{s.unified_item_id}</span>
                            ) : null}
                            {s.leaf_count != null ? (
                              <span>
                                {s.leaf_count} {t("leaves", "ύλες")}
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td style={{ textAlign: "right", fontWeight: 700 }}>
                          {formatSoldUnits(s.qty_sold, t) || "—"}
                        </td>
                        <td style={{ textAlign: "right" }}>{formatEur(s.plate_cost_eur)}</td>
                        <td style={{ textAlign: "right" }}>
                          <div>{formatEur(s.net_unit_price_eur)}</div>
                          {s.net_price_source ? (
                            <div style={{ fontSize: 10, color: "var(--text-muted)", fontWeight: 600 }}>
                              {s.net_price_source === "pos_list_net"
                                ? t("list net", "καθαρή λίστα")
                                : t("catalog", "κατάλογος")}
                            </div>
                          ) : null}
                        </td>
                        <td style={{ textAlign: "right", fontWeight: 700 }}>
                          {formatPct(s.plate_food_cost_pct)}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <div className="procureiq-contrib-forecast-need">
                            {formatEur(s.day_cost_eur)}
                          </div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="procureiq-contrib-detail-row">
                          <td
                            colSpan={7}
                            style={{ padding: 0, borderBottom: "1px solid var(--border)" }}
                          >
                            <div className="procureiq-contrib-sources">
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 8,
                                  marginBottom: 8,
                                  alignItems: "center",
                                }}
                              >
                                <p className="procureiq-contrib-sources-hint" style={{ margin: 0, flex: 1 }}>
                                  {t(
                                    "Raw materials for one plate × sold qty. Click Orders for tickets that included this item.",
                                    "Πρώτες ύλες ανά πιάτο × sold qty. Κλικ στο Orders για παραγγελίες με αυτό το είδος."
                                  )}
                                </p>
                                <button
                                  type="button"
                                  className="btn-secondary"
                                  style={{ fontSize: 12, padding: "4px 10px" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    loadOrders(s);
                                  }}
                                >
                                  {t("Orders", "Παραγγελίες")}
                                </button>
                              </div>
                              {openOrdersKey === ordersCacheKey(s) && (
                                <div
                                  style={{
                                    background: "var(--bg-muted, #f8fafc)",
                                    padding: "8px 12px",
                                    marginBottom: 8,
                                    borderRadius: 8,
                                  }}
                                >
                                  {ordersLoading[ordersCacheKey(s)] && (
                                    <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                      {t("Loading orders…", "Φόρτωση παραγγελιών…")}
                                    </p>
                                  )}
                                  {ordersError[ordersCacheKey(s)] && (
                                    <p style={{ fontSize: 12, color: "var(--danger, #b91c1c)" }}>
                                      {ordersError[ordersCacheKey(s)]}
                                    </p>
                                  )}
                                  {!ordersLoading[ordersCacheKey(s)] &&
                                    !ordersError[ordersCacheKey(s)] &&
                                    ordersByKey[ordersCacheKey(s)] &&
                                    !(ordersByKey[ordersCacheKey(s)].orders || []).length && (
                                      <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                        {t("No orders found", "Δεν βρέθηκαν παραγγελίες")}
                                      </p>
                                    )}
                                  {!ordersLoading[ordersCacheKey(s)] &&
                                    !ordersError[ordersCacheKey(s)] &&
                                    (ordersByKey[ordersCacheKey(s)]?.orders || []).length > 0 && (
                                      <table className="procureiq-contrib-sources-table">
                                        <thead>
                                          <tr>
                                            <th>{t("Order", "Παραγγελία")}</th>
                                            <th>{t("Channel", "Κανάλι")}</th>
                                            <th>{t("Time", "Ώρα")}</th>
                                            <th style={{ textAlign: "right" }}>{t("Qty", "Ποσ.")}</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {ordersByKey[ordersCacheKey(s)].orders.map((o) => {
                                            const label =
                                              o.display_id || o.order_id || o.order_key || "—";
                                            return (
                                              <tr key={`${o.order_key}-${o.channel}`}>
                                                <td style={{ fontWeight: 600 }}>
                                                  {o.order_url ? (
                                                    <a
                                                      href={o.order_url}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      title={o.order_id || o.order_key}
                                                    >
                                                      {label}
                                                    </a>
                                                  ) : (
                                                    label
                                                  )}
                                                </td>
                                                <td>{o.channel}</td>
                                                <td style={{ fontSize: 12 }}>
                                                  {o.order_time
                                                    ? String(o.order_time)
                                                        .replace("T", " ")
                                                        .slice(0, 19)
                                                    : "—"}
                                                </td>
                                                <td style={{ textAlign: "right" }}>
                                                  {Number(o.qty || 0).toFixed(2)}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    )}
                                </div>
                              )}
                              <table className="procureiq-contrib-ingredients-table">
                                <thead>
                                  <tr>
                                    <th>{t("Ingredient", "Συστατικό")}</th>
                                    <th style={{ textAlign: "right" }}>
                                      {t("Per plate", "Ανά πιάτο")}
                                    </th>
                                    <th style={{ textAlign: "right" }}>
                                      {t("Plate €", "€ πιάτου")}
                                    </th>
                                    <th style={{ textAlign: "right" }}>
                                      {t("Day qty", "Ποσ. ημέρας")}
                                    </th>
                                    <th style={{ textAlign: "right" }}>
                                      {t("Day €", "€ ημέρας")}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(s.leaves || []).map((leaf) => {
                                    const unit = String(leaf.base_unit || "").trim();
                                    return (
                                      <tr key={leaf.item_id}>
                                        <td>
                                          <div className="procureiq-contrib-ingredient-name">
                                            {leaf.item_name}
                                            {unit ? (
                                              <span className="procureiq-contrib-ingredient-unit">
                                                {unit}
                                              </span>
                                            ) : null}
                                          </div>
                                          <div className="procureiq-contrib-ingredient-meta">
                                            {leaf.item_id}
                                          </div>
                                        </td>
                                        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                                          {formatQtyWithUnit(leaf.qty_per_unit_base, unit)}
                                        </td>
                                        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                                          {formatEur(leaf.plate_cost_eur)}
                                        </td>
                                        <td
                                          style={{
                                            textAlign: "right",
                                            fontWeight: 700,
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {formatQtyWithUnit(leaf.qty_base_total, unit)}
                                        </td>
                                        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                                          {formatEur(leaf.cost_eur)}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                                <tfoot>
                                  <tr>
                                    <td colSpan={2}>
                                      {t("Plate / day total", "Σύνολο πιάτου / ημέρας")}
                                    </td>
                                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                                      {formatEur(s.plate_cost_eur)}
                                    </td>
                                    <td />
                                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                                      {formatEur(s.day_cost_eur)}
                                    </td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      ) : !leafRows.length ? (
        <div style={{ textAlign: "center", padding: "24px 12px" }}>
          <p style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>
            {t("No material consumption for this day", "Καμία κατανάλωση για αυτή την ημέρα")}
          </p>
        </div>
      ) : (
        <div className="procureiq-trace-table-wrap">
          <table className="procureiq-trace-table">
            <thead>
              <tr>
                <th style={{ width: 28 }} aria-hidden />
                <th>{t("Leaf", "Πρώτη ύλη")}</th>
                <th style={{ textAlign: "right" }}>{t("Day cost", "Κόστος ημέρας")}</th>
                <th style={{ textAlign: "right" }}>{t("Day qty", "Ποσ. ημέρας")}</th>
                <th style={{ textAlign: "right" }}>{t("Sources", "Πηγές")}</th>
              </tr>
            </thead>
            <tbody>
              {leafRows.map((leaf) => {
                const isOpen = Boolean(expanded[leaf.item_id]);
                const unit = String(leaf.base_unit || "").trim();
                const material = contribByLeaf[leaf.item_id];
                const loading = contribLoading[leaf.item_id];
                const err = contribError[leaf.item_id];
                return (
                  <Fragment key={leaf._key}>
                    <tr
                      className="procureiq-contrib-leaf-row"
                      style={{ cursor: "pointer" }}
                      onClick={() => toggleLeaf(leaf.item_id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleLeaf(leaf.item_id);
                        }
                      }}
                      tabIndex={0}
                      aria-expanded={isOpen}
                    >
                      <td style={{ verticalAlign: "middle", paddingRight: 0 }}>
                        <span className={`procureiq-chevron ${isOpen ? "is-open" : ""}`}>›</span>
                      </td>
                      <td>
                        <div style={{ fontWeight: 700 }}>{leaf.item_name}</div>
                        <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                          {leaf.item_id}
                          {unit ? ` · ${unit}` : ""}
                        </div>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <div className="procureiq-contrib-forecast-need">
                          {leaf.cost_eur != null ? `€${Number(leaf.cost_eur).toFixed(2)}` : "—"}
                        </div>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {formatQtyWithUnit(leaf.qty_base_total, unit)}
                      </td>
                      <td style={{ textAlign: "right", fontWeight: 700 }}>
                        {leaf.contrib_count}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="procureiq-contrib-detail-row">
                        <td colSpan={5} style={{ padding: 0, borderBottom: "1px solid var(--border)" }}>
                          <div className="procureiq-contrib-sources">
                            {loading && (
                              <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
                                {t("Loading sources…", "Φόρτωση πηγών…")}
                              </p>
                            )}
                            {err && (
                              <p style={{ fontSize: 13, color: "var(--danger, #b91c1c)" }}>{err}</p>
                            )}
                            {!loading && !err && material && (
                              <>
                                <p className="procureiq-contrib-sources-hint">
                                  {t(
                                    "Top line = leaf qty in item unit. Second = sold plates. Click a sold item for orders that included it.",
                                    "Πάνω = ποσότητα πρώτης ύλης. Κάτω = sold plates. Κλικ σε sold είδος για τις παραγγελίες."
                                  )}
                                </p>
                                <table className="procureiq-contrib-sources-table">
                                  <thead>
                                    <tr>
                                      <th>{t("Sold item", "Sold είδος")}</th>
                                      <th style={{ textAlign: "right" }}>{t("Sold", "Sold")}</th>
                                      <th style={{ textAlign: "right" }}>
                                        {t("Per unit", "Ανά τεμ.")}
                                      </th>
                                      <th style={{ textAlign: "right" }}>{t("Total", "Σύνολο")}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(material.contributions || []).map((c) => {
                                      const okey = ordersCacheKey(c);
                                      const ordersOpen = openOrdersKey === okey;
                                      const odata = ordersByKey[okey];
                                      const oload = ordersLoading[okey];
                                      const oerr = ordersError[okey];
                                      return (
                                        <Fragment key={okey}>
                                          <tr
                                            style={{ cursor: "pointer" }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (ordersOpen) {
                                                setOpenOrdersKey(null);
                                              } else {
                                                loadOrders(c);
                                              }
                                            }}
                                          >
                                            <td>
                                              <div style={{ fontWeight: 600 }}>{c.name}</div>
                                              <div
                                                style={{
                                                  fontSize: 11,
                                                  color: "var(--text-muted)",
                                                  display: "flex",
                                                  flexWrap: "wrap",
                                                  gap: 6,
                                                  alignItems: "center",
                                                  marginTop: 3,
                                                }}
                                              >
                                                {roleBadge(c.line_role, t)}
                                                {c.material_product_id ? (
                                                  <span>{c.material_product_id}</span>
                                                ) : null}
                                              </div>
                                            </td>
                                            <td style={{ textAlign: "right" }}>
                                              {formatSoldUnits(c.qty_sold, t) || "—"}
                                            </td>
                                            <td style={{ textAlign: "right" }}>
                                              {formatQtyWithUnit(c.qty_per_unit_base, unit)}
                                            </td>
                                            <td style={{ textAlign: "right", fontWeight: 700 }}>
                                              {formatQtyWithUnit(c.qty_base_total, unit)}
                                            </td>
                                          </tr>
                                          {ordersOpen && (
                                            <tr>
                                              <td
                                                colSpan={4}
                                                style={{
                                                  background: "var(--bg-muted, #f8fafc)",
                                                  padding: "8px 12px",
                                                }}
                                              >
                                                {oload && (
                                                  <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                                    {t("Loading orders…", "Φόρτωση παραγγελιών…")}
                                                  </p>
                                                )}
                                                {oerr && (
                                                  <p
                                                    style={{
                                                      fontSize: 12,
                                                      color: "var(--danger, #b91c1c)",
                                                    }}
                                                  >
                                                    {oerr}
                                                  </p>
                                                )}
                                                {!oload &&
                                                  !oerr &&
                                                  odata &&
                                                  !(odata.orders || []).length && (
                                                    <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                                      {t(
                                                        "No orders found",
                                                        "Δεν βρέθηκαν παραγγελίες"
                                                      )}
                                                    </p>
                                                  )}
                                                {!oload &&
                                                  !oerr &&
                                                  (odata?.orders || []).length > 0 && (
                                                    <table
                                                      className="procureiq-contrib-sources-table"
                                                      style={{ marginTop: 4 }}
                                                    >
                                                      <thead>
                                                        <tr>
                                                          <th>{t("Order", "Παραγγελία")}</th>
                                                          <th>{t("Channel", "Κανάλι")}</th>
                                                          <th>{t("Time", "Ώρα")}</th>
                                                          <th style={{ textAlign: "right" }}>
                                                            {t("Qty", "Ποσ.")}
                                                          </th>
                                                        </tr>
                                                      </thead>
                                                      <tbody>
                                                        {odata.orders.map((o) => {
                                                          const label =
                                                            o.display_id ||
                                                            o.order_id ||
                                                            o.order_key ||
                                                            "—";
                                                          return (
                                                            <tr key={`${o.order_key}-${o.channel}`}>
                                                              <td style={{ fontWeight: 600 }}>
                                                                {o.order_url ? (
                                                                  <a
                                                                    href={o.order_url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    title={o.order_id || o.order_key}
                                                                  >
                                                                    {label}
                                                                  </a>
                                                                ) : (
                                                                  label
                                                                )}
                                                              </td>
                                                              <td>{o.channel}</td>
                                                              <td style={{ fontSize: 12 }}>
                                                                {o.order_time
                                                                  ? String(o.order_time)
                                                                      .replace("T", " ")
                                                                      .slice(0, 19)
                                                                  : "—"}
                                                              </td>
                                                              <td style={{ textAlign: "right" }}>
                                                                {Number(o.qty || 0).toFixed(2)}
                                                              </td>
                                                            </tr>
                                                          );
                                                        })}
                                                      </tbody>
                                                    </table>
                                                  )}
                                              </td>
                                            </tr>
                                          )}
                                        </Fragment>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
